import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { UrlFormatter } from 'shared';

/**
 * Native-Angular port of the AngularJS `Guests` service
 * (account-v1/src/core/api/guests.js). Verbatim: relative `/api/...` URLs via the
 * native `UrlFormatter.format` (byte-identical query append incl. the v1 `YYYY-M-D`
 * Date serialization the quotes `cutoffDate` relies on), Promise methods
 * (`firstValueFrom` yields the body = the v1 `$http…then(r => r.data)`), and the
 * v1 `angular.extend({propertyUUID}, query)` reproduced as an object spread
 * (same key order → same query-string order).
 *
 * Named `GuestsV1ApiService` (not `GuestsApiService`) to avoid colliding with the
 * partial Observable-based People-feature client of the same v1 factory
 * (app/features/people/services/guests-api.service.ts).
 *
 * The v1 `details(guestUUID, recovery)` passed a `{recovery}` $http config consumed
 * by the v1 recovery interceptor; HttpClient has no equivalent and no native
 * consumer passes it — dropped.
 *
 * WS5 #10 Stage A inc 2d: replaces the `$injector.get('Guests')` bridge.
 * `providedIn:'root'` so consumers `inject()` it directly.
 */
@Injectable({ providedIn: 'root' })
export class GuestsV1ApiService {
  private readonly http = inject(HttpClient);

  /** Returns guest details. */
  details(guestUUID: string): Promise<any> {
    return firstValueFrom(this.http.get<any>(UrlFormatter.format('/api/guests/:guestUUID', { guestUUID }, false)));
  }

  /** Returns property guests given query. */
  guests(propertyUUID: string, query: any): Promise<any> {
    return firstValueFrom(
      this.http.get<any>(UrlFormatter.format('/api/:propertyUUID/properties/:propertyUUID/guests', { propertyUUID, ...query }, true)),
    );
  }

  /** Returns available feedbacks for given query. */
  feedback(propertyUUID: string, query: any): Promise<any> {
    return firstValueFrom(
      this.http.get<any>(UrlFormatter.format('/api/:propertyUUID/properties/:propertyUUID/feedback', { propertyUUID, ...query }, true)),
    );
  }

  /** Returns property photos given query. */
  photos(propertyUUID: string, query: any): Promise<any> {
    return firstValueFrom(
      this.http.get<any>(UrlFormatter.format('/api/:propertyUUID/properties/:propertyUUID/photos', { propertyUUID, ...query }, true)),
    );
  }

  unratedStoriesPhotos(propertyUUID: string, query: any): Promise<any> {
    return firstValueFrom(
      this.http.get<any>(
        UrlFormatter.format('/api/:propertyUUID/properties/:propertyUUID/stories/photos', { propertyUUID, ...query }, true),
      ),
    );
  }

  unratedStoriesNoPhotos(propertyUUID: string, query: any): Promise<any> {
    return firstValueFrom(
      this.http.get<any>(
        UrlFormatter.format('/api/:propertyUUID/properties/:propertyUUID/stories/nophotos', { propertyUUID, ...query }, true),
      ),
    );
  }

  /** Returns property quotes given query. */
  quotes(propertyUUID: string, query: any): Promise<any> {
    return firstValueFrom(
      this.http.get<any>(UrlFormatter.format('/api/:propertyUUID/properties/:propertyUUID/quotes', { propertyUUID, ...query }, true)),
    );
  }

  /** Returns popular keywords. */
  getCommonKeywords(propertyUUID: string): Promise<any> {
    return firstValueFrom(
      this.http.get<any>(UrlFormatter.format('/api/:propertyUUID/properties/:propertyUUID/tags/popular', { propertyUUID }, false)),
    );
  }

  /** Search keyword. */
  searchKeyword(propertyUUID: string, search: string): Promise<any> {
    return firstValueFrom(
      this.http.get<any>(UrlFormatter.format('/api/:propertyUUID/properties/:propertyUUID/tags/search', { propertyUUID, search }, true)),
    );
  }
}
